import { Combobox } from "cerulean";
import filters from "byzantine/src/filters";
import { useLayoutEffect, useState } from "react";
import { countryCodeToFlag } from "byzantine/src/dbbl/utils/internationalPhoneNumber";
import styles from "./InternationalWire.module.scss";

type CurrencySelectorProps = {
  currencies: API.CurrencyItem[];
  onChange: (value: string) => void;
  value: string;
  label: string;
  error: string;
};

const CurrencySelector = ({
  currencies,
  onChange,
  value,
  label,
  error,
}: CurrencySelectorProps) => {
  const selectedCurrency = currencies.find((c) => c.currency === value);
  const [inputValue, setInputValue] = useState<string>(
    selectedCurrency ? selectedCurrency.name : ""
  );

  // whenever `value` changes externally, update inputValue (what is displayed in the input) to match
  useLayoutEffect(() => {
    setInputValue(
      selectedCurrency ? `${filters.humanize(selectedCurrency.name)}` : ""
    );
  }, [value, selectedCurrency]);

  const handleChange = (c: string) => {
    onChange(c);
    const pickedCurrency = currencies.find((r) => r.currency === c);
    setInputValue(
      pickedCurrency ? `${filters.humanize(pickedCurrency.name)}` : ""
    );
  };

  return (
    <div className={styles.currencySelector}>
      <Combobox
        field="currency"
        label={label}
        value={value}
        onChange={handleChange}
        errorText={error}
        inputValue={inputValue}
        onInputChange={(updatedValue: string) => {
          if (!updatedValue && value) {
            return;
          }
          // searchValue is passed into here via updatedValue and is purely in the background
          // we only want the input value to be the humanized name so we strip out the currency code
          const searchSplit = updatedValue.split(",");
          setInputValue(searchSplit.slice(1).join(" ") || updatedValue);
          if (!updatedValue) {
            onChange("");
          }
        }}
      >
        {currencies.map(({ currency, name }) => (
          <Combobox.Item
            key={currency}
            value={currency}
            // the comma separates the currency code from the humanized name for searching purposes
            searchValue={`${currency},${filters.humanize(name)}`}
          >
            <span className={styles.currencyFlag} aria-hidden="true">
              {countryCodeToFlag(currency.slice(0, 2))} {currency}
            </span>
            <span className="padding--left--xs">{filters.humanize(name)}</span>
          </Combobox.Item>
        ))}
      </Combobox>
    </div>
  );
};

export default CurrencySelector;